/**
 * External Imports
 */
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useEffect,
  useState
} from 'react';
import { connect } from 'react-redux';

/**
 * Nexus Imports
 */
import { NeuCard, NeuIcon } from '@neutron/react';

/**
 * Internal Imports
 */
import { RootState } from '../../../../redux/store';

import { date } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { Issue, Stoplight } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CustomPaperClipIcon,
  IdLabel,
  StoplightStatus
} from './AttachedCard.styles';
import { SCLogo } from '../categorySelect/CategorySelect.styles';

/**
 * Static File Imports
 */
import serviceCentralLogoImg from '../../../../assets/images/service_central_icon.png';

interface IAttachedCard {
  attached?: boolean;
  attachToItemCompleted?: boolean;
  item: Issue & Stoplight;
  scrollContainerRef?: RefObject<HTMLDivElement>;
  serviceCentralCategories: string[];
  slide: string;
  type: 'issue' | 'stoplight';
  handleOpen?: (id: string) => void;
  handleRemoveAttached?: (
    event: React.MouseEvent<HTMLNeuIconElement>,
    item: Issue & Stoplight
  ) => void;
  setItemsToAttach: (item: Issue & Stoplight) => void;
  setNewlyAttachedOpen: Dispatch<SetStateAction<boolean>>;
}

const AttachedCard = ({
  attached,
  attachToItemCompleted,
  item,
  scrollContainerRef,
  serviceCentralCategories,
  slide,
  type,
  handleOpen,
  handleRemoveAttached,
  setItemsToAttach,
  setNewlyAttachedOpen
}: IAttachedCard) => {
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (slide === 'hide') setHover(false);
  }, [slide]);

  return (
    <NeuCard
      id={`${type}-card-${type === 'issue' ? item.taskId : item.id}`}
      className={`d-flex mx-0 my-1 py-0${
        attached || !item.isAttached
          ? ' hover-card hover-card__issue'
          : item.isAttached
          ? ' hover-card__disabled'
          : ''
      }`}
      data-testid={`${type}-card-${type === 'issue' ? item.taskId : item.id}`}
      unit="card"
      style={{ minHeight: '9rem' }}
      onClick={
        attached && handleOpen
          ? () => handleOpen(type === 'issue' ? item.taskId : item.id)
          : !item.isAttached
          ? () => {
              setItemsToAttach(item);
              setNewlyAttachedOpen(true);
              if (scrollContainerRef?.current) {
                setTimeout(
                  () =>
                    scrollContainerRef?.current?.parentElement?.scrollTo({
                      top: 0,
                      behavior: 'smooth'
                    }),
                  50
                );
              }
            }
          : undefined
      }
      onMouseOver={() => setHover(true)}
      onMouseLeave={slide === 'hide' ? () => setHover(false) : undefined}
    >
      <NeuCard
        className="d-flex align-items-center justify-content-between"
        unit="content"
      >
        <div className="d-flex flex-column align-items-start overflow-hidden ps-0">
          <div className="d-flex align-items-center mt-1 mb-0 custom-input-heading">
            {type === 'issue' ? (
              item.completed ? (
                'Completed | '
              ) : (
                'Opened | '
              )
            ) : (
              <>
                <NeuIcon
                  color={
                    item.status === 'Completed'
                      ? 'green'
                      : item.status === 'In Progress'
                      ? 'yellow'
                      : 'red'
                  }
                  size="1x"
                >
                  circle
                </NeuIcon>
                <StoplightStatus className="ms-1">{` ${item.status} | `}</StoplightStatus>
              </>
            )}
            <IdLabel className="ms-1">{item.friendlyId || 'Unknown'}</IdLabel>
            {item.completed && item.completedDate
              ? ` - ${date.datePrettier(item.completedDate)}`
              : ''}
          </div>
          <div
            className="d-flex mt-1 mb-0 custom-input-heading bold"
            style={{ minHeight: '1.25rem' }}
          >
            {type === 'issue' &&
              ((item.category &&
                serviceCentralCategories.includes(item.category)) ||
                (item.taskSubtype &&
                  serviceCentralCategories.includes(item.taskSubtype)) ||
                (item.historicCategory &&
                  serviceCentralCategories.includes(
                    item.historicCategory
                  ))) && (
                <SCLogo
                  width="25"
                  alt="service central logo"
                  src={serviceCentralLogoImg}
                />
              )}
            {attached
              ? item.category || item.taskSubtype || 'Unknown'
              : item.category || item.historicCategory || 'Unknown'}
          </div>
          {item.subCategory && (
            <p className="mt-1 mb-0 custom-input-heading bold">
              {item.subCategory}
            </p>
          )}
          <p
            className="mt-1 mb-0 custom-input-heading text-clamp text-clamp__2"
            style={{ minHeight: '3rem' }}
          >
            {type === 'issue' ? item.description : item.details}
          </p>
          {/* <NeuLabel className="mt-0 custom-input-heading bold">
                  Origin: {text.capFirst(issue.roundingType)}
                </NeuLabel> */}
          {/* TODO: Not available on DTO yet */}
          {/* <NeuLabel className="mt-0 custom-input-heading bold">
                  Created By: {issue.userFullName}
                </NeuLabel> */}
          <p className="mt-1 mb-0 custom-input-heading bold">
            Created By:{' '}
            {(type === 'issue' ? item.userFullName : item.createdBy) ||
              'Unknown'}
          </p>
          <p className="mt-1 mb-0 custom-input-heading">
            {/* {date.dayOfYr(new Date())} */}
          </p>
        </div>
        {!attachToItemCompleted && (
          <div
            className="d-flex align-items-center justify-content-end ps-0"
            style={{ flexBasis: '14%' }}
          >
            {attached ? (
              <NeuIcon
                className="ms-2"
                color="red"
                onClick={
                  handleRemoveAttached
                    ? event => handleRemoveAttached(event, item)
                    : undefined
                }
                style={{ cursor: 'pointer' }}
              >
                remove_circle
              </NeuIcon>
            ) : !item.isAttached ? (
              <NeuIcon
                className="ms-2"
                color="blue"
                size="1x"
                style={{ cursor: 'pointer' }}
              >
                {hover ? 'add_circle' : 'add_circle_outline'}
              </NeuIcon>
            ) : (
              <CustomPaperClipIcon className="ms-2" color="gray" size="1x">
                attachment
              </CustomPaperClipIcon>
            )}
          </div>
        )}
      </NeuCard>
    </NeuCard>
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  slide: state.ToastReducer.slide
});

export default connect(mapReduxStateToProps)(AttachedCard);
